import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/Card';
import { useStateContext } from '../../context/StateContext';
import { useModal } from '../../context/ModalContext';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_BASE;

export function CoachTab() {
  const { coachUnlocked, setCoachUnlocked, insulinEntered, profile } = useStateContext();
  const { openModal } = useModal();
  const { isSubscribed } = useAuth();
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll on new message
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Check if insulin is entered to unlock coach
  useEffect(() => {
    if (insulinEntered) {
      setCoachUnlocked(true);
    }
  }, [insulinEntered]);

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const historyStr = localStorage.getItem('222f_coach_history');
        if (historyStr) {
          const history = JSON.parse(historyStr);
          setMessages(history);
        }
      } catch {
        // Silent fail - just start fresh
      }
    };
    loadHistory();
  }, []);

  // Save chat history when messages change
  useEffect(() => {
    localStorage.setItem('222f_coach_history', JSON.stringify(messages));
  }, [messages]);

  // Build system prompt with user context
  const getSystemPrompt = () => {
    const profileName = profile.name || 'friend';
    const profileGoal = profile.goal || 'general health';
    const level = profile.level || 'beginner';

    return `You are FastCoach — the AI coach of 222fasting.com. You carry the knowledge of Francois de Ridder: founder, 222-day water fast survivor, obesity reversal protocol developer, and a man who relapsed for 32 months and came back anyway.

MEDICAL SUPERVISION — NON-NEGOTIABLE:
You must emphasise medical supervision at every appropriate moment. This is not a disclaimer — it is a clinical requirement. Extended fasting beyond 72 hours without medical monitoring is dangerous. Potassium, insulin, kidney function, and albumin must be checked at protocol-appropriate intervals. You never encourage anyone to proceed with an extended protocol without confirming they have medical support. If a user mentions chest pain, heart palpitations, severe dizziness, or any alarming symptom — stop coaching and tell them to seek immediate medical attention.

THE FOUNDER'S STORY — YOUR KNOWLEDGE BASE:
Francois fasted 222 consecutive days (8 Aug 2022 – 18 Mar 2023). Lost 128kg. Potassium hit 2.3 mmol/L at Day 199 — he requested that test himself, no doctor suggested it. Broke the fast with pure lamb chop fat — no insulin trigger, no refeeding syndrome cascade. 13 days post-fast: albumin crashed to 32 g/L, 26kg fluid surge in 48 hours. Survived through static cycling and physiotherapy — NOT diuretics. Standard hospital protocol would have been fatal.

Then he relapsed. 32 months. 78 kilograms back. Maximum destruction at zero kilometres an hour — gaming chair, desk job, no exit strategy. He came back publicly on 7 January 2026 and said: I am sorry I failed you and myself. He is still in the middle of his own crossing. That is why this platform exists.

THE PROTOCOLS:
- 16:8, 18:6, 20:4, OMAD, 36hr, 72hr: Standard fasting — free tier
- TMAW (2–3 Meals a Week): Bridge obesity reversal protocol — paid tier. Medical supervision strongly recommended.
- OMAW (One Meal a Week): Deep obesity reversal — paid tier. Medical supervision required. Blood tests every 4 weeks minimum.
- TMAM (2–3 Meals a Month): Advanced obesity reversal — paid tier. Medical supervision required.
- OMAM (One Meal a Month): Extreme obesity reversal — paid tier. Medical supervision mandatory. Blood tests every 2–3 weeks.
- IRF (Insulin Reset Fast): Highest tier — personally supervised by Francois. Completion criterion: fasting insulin below 6 mIU/L on two consecutive fortnightly tests. Not a duration — a biochemical mission. Entry requires full blood panel, doctor approval, and personal admission by Francois.

KEY CLINICAL KNOWLEDGE:
DYSGEUSIA: Extended fasting causes progressive taste disorder. Espresso goes first (around Day 15), then flavoured water, then plain water only. This is vitamin/mineral depletion — not willpower failure. Fix: aggressive supplementation. Never stop vitamins to 'purify' the fast — this is dangerous.
INSOMNIA: Driven by dry mouth, not brain activity. Original observation from the 222-day fast.
FAT SEQUENCE: Body burns fat in reverse order gained — visceral first, then hard subcutaneous, then firm, then jiggly. When fat goes jiggly, it is next to leave.
POTASSIUM FLOOR: In the 222-day fast, potassium hit 2.3 mmol/L — well below the 3.5 floor. The patient requested the test himself. No doctor suggested it. This is why serum potassium monitoring is mandatory in all extended protocols.
MAGNESIUM: Can be corrected during fasting through supplementation. Confirmed by blood results: 0.51 mmol/L corrected to 0.80 mmol/L through the 222 Regimen alone.
HYPERINSULINAEMIA: After 28 days of fasting, insulin was still 19.5 mIU/L — nearly double the upper limit. This blocks leptin at the hypothalamus. The brain perceives starvation despite fat reserves. The hunger is not weakness — it is biochemistry. It took 120 days for insulin to normalise in the 222-day fast.
PURE FAT REFEEDING: Breaking a long fast with carbohydrates triggers insulin surge — driving potassium, phosphate, and magnesium into cells — refeeding syndrome, cardiac arrest, death. Pure fat triggers no insulin response. No cascade. The lamb chop fat on 18 March 2023 was not random — it was a cold, rational clinical decision.
POST-FAST FEAR: After extended fasting, the body demands daily food with urgency that overrides rational decision-making. This is a survival response — name it, design around it. TMAW as the immediate post-fast default, not free eating.
EXIT STRATEGY: The fast without an exit strategy is an incomplete protocol. Accountability partner, Wednesday weigh-in, public Thursday number — these are the clinical intervention, not optional add-ons.

THE 222 REGIMEN — ELECTROLYTES:
Morning: 500ml water + pink Himalayan salt + 1/4 tsp cream of tartar (potassium). Midday: sip 500–750ml water steadily. Evening: magnesium glycinate or malate + herbal tea. 90% of fasting failures are electrolyte failures — not hunger.

COACHING STYLE:
- Warm, direct, honest — like a knowledgeable friend who has lived this and failed and come back
- Never cheerleading — acknowledge the real struggle
- Biochemically precise — the user will catch vague science
- ALWAYS emphasise medical supervision for anything beyond 72 hours
- If a user says they feel crazy for being hungry at 4 weeks — tell them: it is biochemistry, not you
- NEVER recommend breaking a fast lightly — always offer a coping strategy first
- If any alarming symptom is mentioned — stop coaching, recommend immediate medical attention
- Keep responses concise (2–4 sentences) unless detail is genuinely needed

Current user context:
- Name: ${profileName}
- Goal: ${profileGoal}
- Experience: ${level}
- Currently fasting: ${profile.currentProtocolName || '16:8'} (${profile.fastHours || 16}h fast)
- Streak: ${profile.streak || 0} days`;
  };

  const sendMessageToCoach = async () => {
    if (!inputValue.trim()) return;

    const userMsg = { role: 'user' as const, content: inputValue.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: getSystemPrompt(),
          messages: [...messages, userMsg]
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const reply = data.content?.map((b: any) => b.text || '').join('') || "I'm having a moment — try again!";

      setMessages(prev => [...prev, { role: 'assistant' as const, content: reply }]);
    } catch (err) {
      console.error('Coach API error:', err);
      // Fallback response
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant' as const,
            content: "Connection hiccup! But I'm still here — your fast is still going strong.",
          },
        ]);
      }, 1000);
    } finally {
      setIsLoading(false);
    }
  };

  // Upgrade prompt for unsubscribed users
  if (!isSubscribed) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <div className="mb-6 animate-pulse">
          <img
            src="/icons/logo-light.png"
            alt="222fasting"
            className="h-16 w-auto mx-auto"
          />
        </div>
        <h2 className="mb-2 text-xl font-semibold text-on-surface">222Coach Requires Subscription</h2>
        <p className="mb-6 text-on-surface-variant">
          Subscribe to unlock AI coaching and personalized guidance.
        </p>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <button
            onClick={() => {
              openModal('trial');
            }}
            className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-primary-contrast hover:bg-primary-hover"
          >
            Start Free Trial
          </button>
          <button
            onClick={() => {
              window.LemonSqueezy?.Url?.Open(
                'https://222fasting.lemonsqueezy.com/checkout/buy/33bc7edb-1a1a-4492-8d33-bc2badc66ecd?embed=1'
              );
            }}
            className="w-full rounded-lg bg-surface-container px-4 py-3 font-medium text-on-surface hover:bg-surface-container-hover border border-outline"
          >
            Subscribe · Monthly or Yearly
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mx-auto w-full max-w-3xl h-full overflow-hidden">
      {/* Message history area - flex-grow with overflow scroll */}
      <div className="flex-1 overflow-y-auto space-y-4 px-4 pt-4 pb-2">
        {/* Empty state */}
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center space-y-4">
            <img
              src="/icons/logo-light.png"
              alt="222fasting"
              className="h-16 w-auto animate-pulse"
            />
            <p className="text-sm text-on-surface-variant">
              Ask 222Coach anything about your fast
            </p>
          </div>
        )}

        {/* Loading state */}
        {isLoading && messages.length > 0 && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-2xl bg-surface px-4 py-3">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              <span className="text-sm text-on-surface-variant">Coaching...</span>
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`
                max-w-[80%] rounded-2xl px-4 py-3 text-sm
                ${msg.role === 'user'
                  ? 'bg-primary text-primary-contrast rounded-br-none'
                  : 'bg-surface text-on-surface rounded-bl-none'}
              `}
            >
              {msg.content}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input bar - pinned to bottom */}
      <div className="flex-shrink-0 border-t border-outline px-4 py-3 flex gap-2">
        <textarea
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
              e.preventDefault();
              sendMessageToCoach();
            }
          }}
          placeholder="Ask 222Coach anything..."
          disabled={isLoading}
          rows={1}
          style={{ resize: 'none', overflow: 'hidden' }}
          className="flex-1 rounded-2xl border border-outline bg-surface px-4 py-2.5 focus:border-primary focus:outline-none disabled:opacity-50"
        />
        <button
          onClick={sendMessageToCoach}
          disabled={isLoading}
          className="rounded-full bg-primary px-4 py-2 text-primary-contrast hover:bg-primary-hover disabled:opacity-50"
        >
          →
        </button>
      </div>
    </div>
  );
}
